import React, { useCallback, useLayoutEffect } from 'react';
import { useNavigation } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { AnimatedViews, theme, useResponsive } from '@sg/garnish';

import {
  CustomizationBottomBar,
  CustomizationContainer,
  CustomizationDetailsCompoundContainer,
  CustomizationFloatingBar,
} from '@order/features-customization';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationLoading = () => {
  const { currentBreakpoint } = useResponsive();
  const { setOptions } = useNavigation();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const hideNavigationHeaderOnXS = useCallback(() => {
    setOptions({ headerShown: !currentBreakpoint.isXS });
  }, [currentBreakpoint.isXS, setOptions]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useLayoutEffect(hideNavigationHeaderOnXS, [hideNavigationHeaderOnXS]);

  // ───────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return <CustomizationLoadingXS />;
  }

  return <CustomizationLoadingSM />;
};

// ─── Components ──────────────────────────────────────────────────────────────

const CustomizationLoadingXS = () => {
  return (
    <CustomizationContainer>
      <View style={styles.headerXS}>
        <AnimatedViews.Pulse>
          <View style={styles.titleRowXS}>
            <View style={styles.titleXS} />
          </View>

          <View style={styles.tabsXS}>
            <View style={styles.activeTab} />

            <View style={styles.tab}>
              <View style={styles.tabLabel} />
            </View>
          </View>
        </AnimatedViews.Pulse>
      </View>

      <View style={styles.ingredientsXS}>
        <AnimatedViews.Pulse index={1} style={styles.sectionHeadingRow}>
          <View style={styles.sectionHeadingXS} />
          <View style={styles.sectionCount} />
        </AnimatedViews.Pulse>

        <IngredientsGridPlaceholder itemsPerRow={3} rows={3} />
      </View>

      <CustomizationFloatingBar>
        <AnimatedViews.Pulse style={styles.floatingControlsXS}>
          <View style={styles.nutritionButtonXS} />

          <View style={styles.floatingButtonsXS}>
            <View style={styles.backButtonXS} />
            <View style={styles.addToBagButtonXS} />
          </View>
        </AnimatedViews.Pulse>
      </CustomizationFloatingBar>
    </CustomizationContainer>
  );
};

const CustomizationLoadingSM = () => {
  const { match } = useResponsive();

  const itemsPerRow = match([3, 2, 4, 5]);
  const sectionIndexes = [...Array(PLACEHOLDER_SECTIONS).keys()];

  return (
    <CustomizationContainer>
      <CustomizationDetailsCompoundContainer.Aside>
        <CustomizationDetailsCompoundContainer.ScrollView scrollEnabled={false}>
          <AnimatedViews.Pulse style={styles.asideHeader}>
            <View style={styles.backButtonSM} />
            <View style={styles.asideTitle} />
          </AnimatedViews.Pulse>

          {sectionIndexes.map((index) => (
            <AnimatedViews.Pulse key={index} index={index + 1} style={styles.selectedSection}>
              <View style={styles.sectionHeadingRow}>
                <View style={styles.selectedSectionHeading} />
                <View style={styles.sectionCount} />
              </View>

              <View style={styles.selectedIngredient}>
                <View style={styles.selectedIngredientImage} />
                <View style={styles.selectedIngredientName} />
              </View>

              <View style={styles.selectedIngredient}>
                <View style={styles.selectedIngredientImage} />
                <View style={styles.addIngredientLabel} />
              </View>
            </AnimatedViews.Pulse>
          ))}
        </CustomizationDetailsCompoundContainer.ScrollView>
      </CustomizationDetailsCompoundContainer.Aside>

      <View style={styles.ingredientsSM}>
        <View style={styles.ingredientsNav}>
          <AnimatedViews.Pulse style={styles.ingredientsNavItems}>
            <View style={styles.activeNavItem} />

            {NAV_ITEM_WIDTHS.map((width) => (
              <NavItemPlaceholder key={width} width={width} />
            ))}
          </AnimatedViews.Pulse>
        </View>

        <View style={styles.ingredientsContentSM}>
          <AnimatedViews.Pulse index={1} style={styles.sectionHeadingSM} />

          <IngredientsGridPlaceholder itemsPerRow={itemsPerRow} rows={2} />
        </View>

        <CustomizationBottomBar>
          <AnimatedViews.Pulse style={styles.bottomControlsSM}>
            <View style={styles.nutritionButtonSM} />
            <View style={styles.addToBagButtonSM} />
          </AnimatedViews.Pulse>
        </CustomizationBottomBar>
      </View>
    </CustomizationContainer>
  );
};

const IngredientsGridPlaceholder = (props: IngredientsGridPlaceholderProps) => {
  const { itemsPerRow, rows } = props;

  const { match } = useResponsive();

  const rowIndexes = [...Array(rows).keys()];
  const cellIndexes = [...Array(itemsPerRow).keys()];

  // ─── Styles ────────────────────────────────────────────────────────────────

  const gridStyle = match([styles.gridXS, styles.gridSM]);
  const gridRowStyle = match([styles.gridRowXS, styles.gridRowSM]);
  const ingredientNameStyle = match([styles.ingredientNameXS, styles.ingredientNameSM]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={gridStyle}>
      {rowIndexes.map((rowIndex) => (
        <View key={rowIndex} style={gridRowStyle}>
          {cellIndexes.map((cellIndex) => (
            <AnimatedViews.Pulse
              key={cellIndex}
              index={rowIndex * itemsPerRow + cellIndex + 2}
              style={styles.ingredient}
            >
              <View style={styles.ingredientImage} />
              <View style={ingredientNameStyle} />
            </AnimatedViews.Pulse>
          ))}
        </View>
      ))}
    </View>
  );
};

const NavItemPlaceholder = (props: NavItemPlaceholderProps) => {
  const { width } = props;

  const navItemDynamicStyle = useStyle(() => ({ width }), [width]);
  const navItemStyles = [styles.navItem, navItemDynamicStyle];

  return <View style={navItemStyles} />;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_SECTIONS = 4;
const NAV_ITEM_WIDTHS = [64, 48, 72, 144, 152, 56];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerXS: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
    backgroundColor: theme.colors.CREAM,
  },
  titleRowXS: {
    height: 72,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleXS: {
    width: 200,
    height: 20,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  tabsXS: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  activeTab: {
    flex: 1,
    height: 34,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
  },
  tabLabel: {
    width: 44,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientsXS: {
    flex: 1,
    overflow: 'hidden',
    paddingTop: theme.spacing['8'],
    paddingHorizontal: theme.spacing['4'],
    backgroundColor: theme.colors.OATMEAL,
  },
  sectionHeadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionHeadingXS: {
    width: 88,
    height: 20,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  sectionCount: {
    width: 20,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  floatingControlsXS: {
    rowGap: theme.spacing['4'],
  },
  nutritionButtonXS: {
    width: 56,
    height: 56,
    alignSelf: 'flex-end',
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  floatingButtonsXS: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButtonXS: {
    width: 56,
    height: 56,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addToBagButtonXS: {
    width: 215,
    height: 56,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  asideHeader: {
    height: 80,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['5'],
    paddingHorizontal: theme.spacing['4'],
    marginBottom: theme.spacing['4'],
  },
  backButtonSM: {
    width: 36,
    height: 36,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  asideTitle: {
    width: 216,
    height: 16,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  selectedSection: {
    rowGap: theme.spacing['4'],
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['10'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.LIGHTER,
  },
  selectedSectionHeading: {
    width: 72,
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  selectedIngredient: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
    paddingHorizontal: theme.spacing['2'],
  },
  selectedIngredientImage: {
    width: 30,
    height: 30,
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  selectedIngredientName: {
    width: 144,
    height: 16,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addIngredientLabel: {
    width: 96,
    height: 16,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientsSM: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.OATMEAL,
  },
  ingredientsNav: {
    height: 80,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['10'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
    overflow: 'hidden',
    backgroundColor: theme.colors.CREAM,
  },
  ingredientsNavItems: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 28,
  },
  activeNavItem: {
    width: 67,
    height: 32,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  navItem: {
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientsContentSM: {
    maxWidth: 1040,
    paddingTop: theme.spacing['10'],
    paddingHorizontal: theme.spacing['10'],
  },
  sectionHeadingSM: {
    width: 104,
    height: 20,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  bottomControlsSM: {
    alignItems: 'flex-end',
    rowGap: theme.spacing['3'],
  },
  nutritionButtonSM: {
    width: 64,
    height: 64,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addToBagButtonSM: {
    width: 287,
    height: 72,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  gridXS: {
    rowGap: theme.spacing['8'],
    marginTop: theme.spacing['4'],
  },
  gridSM: {
    rowGap: theme.spacing['6'],
    marginTop: theme.spacing['4'],
  },
  gridRowXS: {
    flexDirection: 'row',
    columnGap: theme.spacing['2'],
  },
  gridRowSM: {
    flexDirection: 'row',
    columnGap: theme.spacing['4'],
  },
  ingredient: {
    flex: 1,
    alignItems: 'center',
  },
  ingredientImage: {
    width: '100%',
    aspectRatio: 0.88,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientNameXS: {
    width: '72%',
    height: 10,
    marginTop: 11,
    marginBottom: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ingredientNameSM: {
    width: '72%',
    height: 16,
    marginTop: 14,
    marginBottom: 6,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type IngredientsGridPlaceholderProps = {
  itemsPerRow: number;
  rows: number;
};

type NavItemPlaceholderProps = {
  width: number;
};
